import React, { useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

function MyModal({ openInModal, children, OTPModal }) {
  if (!(OTPModal === 1 || OTPModal === 2 || OTPModal === true) && !openInModal) {
    return <></>;
  }

  if (!openInModal) {
    return children;
  }

  return <Modal show={OTPModal === 1 || OTPModal === 2 || OTPModal === true}>{children}</Modal>;
}

function MyModalHeader({ children, closeButton, openInModal }) {
  if (!openInModal) {
    // Attractive header styling for inline mode
    return (
      <div
        style={{
          background: "linear-gradient(90deg, #6e5a95 0%, #886cc1 100%)",
          color: "#fff",
          padding: "18px 24px",
          borderRadius: "12px 12px 0 0",
          marginBottom: 0,
          fontWeight: 700,
          fontSize: "1.5rem",
          letterSpacing: "0.5px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {children}
      </div>
    );
  }
  return (
    <Modal.Header closeButton={closeButton}>
      <Modal.Title className="otp-title">{children}</Modal.Title>
    </Modal.Header>
  );
}

function MyModalBody({ children, openInModal }) {
  if (!openInModal) {
    // Attractive body styling for inline mode
    return (
      <div
        style={{
          background: "#f9fafd",
          padding: "32px 24px",
          borderRadius: "0 0 12px 12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          marginBottom: "0",
        }}
      >
        {children}
      </div>
    );
  }
  return <Modal.Body>{children}</Modal.Body>;
}

function MyModalFooter({ children, openInModal }) {
  if (!openInModal) {
    // Attractive footer styling for inline mode
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          background: "#fff",
          padding: "18px 24px",
          borderRadius: "0 0 12px 12px",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.03)",
          marginTop: "-16px",
        }}
      >
        {children}
      </div>
    );
  }
  return <Modal.Footer>{children}</Modal.Footer>;
}

export const OTPModalUI = ({ OTPModal, setOTPModal, resendOTP, OTPField, setOTPField, inputRefs, handleChangeOTP, counter, setCounter, verifyEmail, newEmail, openInModal = true }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (OTPModal === 1 || OTPModal === 2 || OTPModal === true) {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [OTPModal === 1 || OTPModal === 2 || OTPModal === true]);

  // Add this effect to focus first input when OTPModal becomes 2
  useEffect(() => {
    if (OTPModal === 2 && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [OTPModal]);

  useEffect(() => {
    let intervalId;
    if (counter > 0) {
      intervalId = setInterval(() => {
        setCounter(counter - 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [counter]);

  const handleKeyDownOTP = (index, e) => {
    if (e.key === "Backspace" && !OTPField[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };
  const handleSendOTP = async () => {
    // For each OTP input, call handleChangeOTP with index and a synthetic event
    for (let i = 0; i < OTPField.length; i++) {
      await handleChangeOTP(i, { target: { value: OTPField[i] } });
    }

    if (OTPModal === 1) {
      verifyEmail(2);
      setOTPModal(2);

      setOTPField(new Array(4).fill(""));
    } else if (OTPModal === 2) {
      setOTPModal(0);
      setOTPField(new Array(4).fill(""));
      setCounter(300);
    }
  };

  // Check if all OTP fields are filled
  const isOTPComplete = OTPField.every((item) => item && item.length === 1);

  return (
    <MyModal {...{ openInModal, OTPModal }}>
      <MyModalHeader {...{ openInModal, closeButton: true }}>{t("Please Enter your OTP Here")}</MyModalHeader>
      <MyModalBody {...{ openInModal }}>
        {OTPModal === 2 ? (
          <h6>
            {t(`Please check your new email inbox `)} <b>{newEmail}</b> {t(` for the one-time code. Once you've received it, enter it in the OTP box to proceed.`)}
          </h6>
        ) : (
          <h6>{t("Please check your email inbox for the one-time code. Once you've received it, enter it in the OTP box to proceed.")}</h6>
        )}
        <div className="text-center otp-popup" style={{ marginTop: "14px" }}>
          {OTPField?.map((item, index) => (
            <input
              key={index}
              type="text"
              value={item}
              ref={(input) => (inputRefs.current[index] = input)}
              onKeyDown={(e) => handleKeyDownOTP(index, e)}
              onChange={(e) => {
                // Only allow single digit input
                const val = e.target.value.replace(/[^0-9a-zA-Z]/g, "").slice(0, 1);
                const newOTP = [...OTPField];
                newOTP[index] = val;
                setOTPField(newOTP);
                if (val && index < OTPField.length - 1) {
                  inputRefs.current[index + 1]?.focus();
                }
              }}
              style={{
                width: "50px",
                margin: "5px",
                height: "50px",
                textAlign: "center",
                fontWeight: "bold",
              }}
            />
          ))}
        </div>
      </MyModalBody>

      <MyModalFooter {...{ openInModal }}>
        <Button
          variant="outline-primary"
          onClick={() => {
            setOTPModal(0);
            setOTPField(new Array(4).fill(""));
            setCounter(300);
          }}
        >
          {t("Close")}
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => {
            if (counter === 0) {
              setCounter(300);
              resendOTP();
            }
          }}
          disabled={counter > 0}
        >
          {counter > 0 ? t("Resend in") + " " + `${counter}` + " " + t("seconds") : t("Resend")}
        </Button>
        <Button variant="primary" onClick={handleSendOTP} disabled={!isOTPComplete}>
          {t("Verify OTP")}
        </Button>
      </MyModalFooter>
    </MyModal>
  );
};
